import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

// ─── Sanity Client Configuration ──────────────────────────────
// Replace these with your actual Sanity project details.
// Until Sanity is set up, the app uses local sample data (see below).
const SANITY_PROJECT_ID = process.env.REACT_APP_SANITY_PROJECT_ID || '';
const SANITY_DATASET = process.env.REACT_APP_SANITY_DATASET || 'production';
const SANITY_API_VERSION = '2024-01-01';

const isSanityConfigured = Boolean(SANITY_PROJECT_ID);

export const sanityClient = isSanityConfigured
  ? createClient({
      projectId: SANITY_PROJECT_ID,
      dataset: SANITY_DATASET,
      useCdn: true,
      apiVersion: SANITY_API_VERSION,
    })
  : null;

// ─── Image URL Builder ────────────────────────────────────────
const builder = sanityClient ? imageUrlBuilder(sanityClient) : null;

export function urlFor(source) {
  if (!builder || !source) return '';
  return builder.image(source).url();
}

// ─── GROQ Queries ─────────────────────────────────────────────
export const queries = {
  allPosts: `*[_type == "blogPost"] | order(publishedAt desc) {
    _id, title, slug, publishedAt, excerpt, coverImage, tags, author
  }`,

  postBySlug: `*[_type == "blogPost" && slug.current == $slug][0] {
    _id, title, slug, publishedAt, content, coverImage, tags, author, language, featuredCards
  }`,

  postsByTag: `*[_type == "blogPost" && $tag in tags] | order(publishedAt desc) {
    _id, title, slug, publishedAt, excerpt, coverImage, tags, author
  }`,

  allSampleDecks: `*[_type == "sampleDeck"] | order(coalesce(sortOrder, 9999) asc, publishedAt desc) {
    _id, slug, name, description, deckCode,
    coverImage, coverImageUrl,
    badges, tags, archetype, difficulty, characters, cardSet,
    "videoCount": count(videos),
    featured, sortOrder, publishedAt, author, creditUrl
  }`,

  sampleDeckBySlug: `*[_type == "sampleDeck" && slug.current == $slug][0] {
    _id, slug, name, description, deckCode,
    coverImage, coverImageUrl,
    badges, tags, archetype, difficulty, characters, cardSet,
    videos, strategy,
    featured, sortOrder, publishedAt, author, creditUrl
  }`,
};

// Sample posts removed - using Sanity CMS for content

// ─── Fetch Helpers ────────────────────────────────────────────
// These functions fetch from Sanity when configured, otherwise
// return empty data with a Coming Soon state.

export async function fetchAllPosts({ tag } = {}) {
  if (isSanityConfigured) {
    try {
      if (tag) {
        return await sanityClient.fetch(queries.postsByTag, { tag });
      }
      return await sanityClient.fetch(queries.allPosts);
    } catch (err) {
      console.error('Sanity fetch failed:', err);
    }
  }
  // No fallback - return empty array
  return [];
}

export async function fetchPostBySlug(slug) {
  if (isSanityConfigured) {
    try {
      return await sanityClient.fetch(queries.postBySlug, { slug });
    } catch (err) {
      console.error('Sanity fetch failed:', err);
    }
  }
  // No fallback - return null
  return null;
}

export function getAllTags() {
  // TODO: Query unique tags from Sanity when content exists
  return [];
}

// ─── Sample Decks ─────────────────────────────────────────────
// Returns the legacy `id` / `imageUrl` / `name_ja` shape expected by
// existing UI code, plus the new fields (badges, tags, videos, …).
function normalizeDeck(doc) {
  if (!doc) return null;
  const slug = doc.slug?.current || doc._id;
  const coverUrl =
    (doc.coverImage ? urlFor(doc.coverImage) : '') || doc.coverImageUrl || '';
  return {
    // Legacy shape
    id: slug,
    name: doc.name?.en || slug,
    name_ja: doc.name?.ja || undefined,
    name_ko: doc.name?.ko || undefined,
    name_zhHant: doc.name?.zhHant || undefined,
    description: doc.description?.en || '',
    description_ja: doc.description?.ja || undefined,
    description_ko: doc.description?.ko || undefined,
    description_zhHant: doc.description?.zhHant || undefined,
    imageUrl: coverUrl,
    deckCode: doc.deckCode || '',
    // New fields
    slug,
    badges: doc.badges || [],
    tags: doc.tags || [],
    archetype: doc.archetype || null,
    difficulty: doc.difficulty || null,
    characters: doc.characters || [],
    cardSet: doc.cardSet || null,
    videos: doc.videos || [],
    videoCount: typeof doc.videoCount === 'number' ? doc.videoCount : (doc.videos?.length || 0),
    strategy: doc.strategy || null,
    featured: !!doc.featured,
    publishedAt: doc.publishedAt || null,
    author: doc.author || 'Gardeno Games',
    creditUrl: doc.creditUrl || null,
  };
}

export async function fetchSampleDecks() {
  if (!isSanityConfigured) return [];
  try {
    const docs = await sanityClient.fetch(queries.allSampleDecks);
    return (docs || []).map(normalizeDeck).filter(Boolean);
  } catch (err) {
    console.error('Sanity fetch (sample decks) failed:', err);
    return [];
  }
}

export async function fetchSampleDeckBySlug(slug) {
  if (!isSanityConfigured) return null;
  try {
    const doc = await sanityClient.fetch(queries.sampleDeckBySlug, { slug });
    return normalizeDeck(doc);
  } catch (err) {
    console.error('Sanity fetch (sample deck by slug) failed:', err);
    return null;
  }
}
